import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";

import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import GoogleCallback from "./components/GoogleCallback";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { PodcastProvider } from "./lib/PodcastContext";
import PodcastPlayer from "./components/PodcastPlayer";

import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MyPayouts from "./pages/MyPayouts";
import Dashboard from "./pages/Dashboard";
import Parcours from "./pages/Parcours";
import Module from "./pages/Module";
import Terrain from "./pages/Terrain";
import Leaderboard from "./pages/Leaderboard";
import Profil from "./pages/Profil";
import Admin from "./pages/Admin";
import Equipe from "./pages/Equipe";
import Onboarding from "./pages/Onboarding";
import SimulatorHome from "./pages/SimulatorHome";
import SimulatorSession from "./pages/SimulatorSession";
import Associations from "./pages/Associations";
import Association from "./pages/Association";
import PartnerSpacesHub from "./pages/PartnerSpacesHub";
import PartnerSpace from "./pages/PartnerSpace";
import AdminPartnerSpaces from "./pages/admin/AdminPartnerSpaces";
import AssoAdmin from "./pages/AssoAdmin";
import AdminInvitations from "./pages/admin/AdminInvitations";
import Cooptation from "./pages/Cooptation";
import CandidateWaiting from "./pages/CandidateWaiting";
import PointsRules from "./pages/PointsRules";
import PointsHistory from "./pages/PointsHistory";
import Layout from "./components/Layout";

function AppRouter() {
    return (
        <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/google" element={<GoogleCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/parcours"
                element={
                    <ProtectedRoute>
                        <Parcours />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/module/:id"
                element={
                    <ProtectedRoute>
                        <Module />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/terrain"
                element={
                    <ProtectedRoute>
                        <Terrain />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/associations"
                element={
                    <ProtectedRoute>
                        <Associations />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/associations/:id"
                element={
                    <ProtectedRoute>
                        <Association />
                    </ProtectedRoute>
                }
            />
            {/* Iter-27.63 — Espace Partenaires (séparé de la formation) */}
            <Route
                path="/espace-partenaires"
                element={
                    <ProtectedRoute>
                        <PartnerSpacesHub />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/espace-partenaires/:id"
                element={
                    <ProtectedRoute>
                        <PartnerSpace />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/espace-partenaires"
                element={
                    <ProtectedRoute adminOnly>
                        <AdminPartnerSpaces />
                    </ProtectedRoute>
                }
            />
            {/* Iter-27.64 — Backoffice dédié aux comptes "association" */}
            <Route
                path="/asso-admin"
                element={
                    <ProtectedRoute allowedRoles={["association"]}>
                        <AssoAdmin />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/leaderboard"
                element={
                    <ProtectedRoute>
                        <Leaderboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profil"
                element={
                    <ProtectedRoute>
                        <Profil />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute adminOnly>
                        <Admin />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/equipe"
                element={
                    <ProtectedRoute allowedRoles={["admin", "manager"]}>
                        <Equipe />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/invitations"
                element={
                    <ProtectedRoute allowedRoles={["admin", "manager"]}>
                        <Layout title="Invitations" subtitle="Gère les accès à la plateforme">
                            <AdminInvitations />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/cooptation"
                element={
                    <ProtectedRoute>
                        <Cooptation />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/regles"
                element={
                    <ProtectedRoute>
                        <PointsRules />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/historique"
                element={
                    <ProtectedRoute>
                        <PointsHistory />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/paiements"
                element={
                    <ProtectedRoute>
                        <MyPayouts />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/candidate-waiting"
                element={
                    <ProtectedRoute skipOnboarding>
                        <CandidateWaiting />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/onboarding"
                element={
                    <ProtectedRoute skipOnboarding>
                        <Onboarding />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/simulator"
                element={
                    <ProtectedRoute>
                        <SimulatorHome />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/simulator/session/:id"
                element={
                    <ProtectedRoute>
                        <SimulatorSession />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

export default function App() {
    useEffect(() => {
        document.documentElement.classList.add("dark");
    }, []);
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <ConfirmProvider>
                        <PodcastProvider>
                            <AppRouter />
                            <PodcastPlayer />
                            <Toaster
                                theme="dark"
                                position="bottom-center"
                                offset={90}
                                toastOptions={{
                                    style: {
                                        background: "rgba(24,24,24,0.95)",
                                        border: "1px solid rgba(238,114,46,0.4)",
                                        color: "#fff",
                                        fontFamily: "Montserrat",
                                    },
                                }}
                            />
                        </PodcastProvider>
                    </ConfirmProvider>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}
